import { useEffect, useRef } from "react";
import PageEditorActionLister from "../../components/PageActionLister/PageEditorActionLister";
import "./RightPanel.css";
import { Properties } from "./Properties";
import { BaseObject } from "../../types";
import {
  LessonPagesActions,
  useLessonPage,
  useLessonPagesDispatch,
  useLessonPagesState,
} from "../../contexts/LessonPagesProvider/LessonPagesProvider";
import { useObjectsState } from "../../contexts/ObjectsProvider";
import { AdvancedPanel } from "../Advanced";
import { useMiscUI } from "../../contexts/MiscUI/MiscUIProvider";
import { OBJECT_TYPE_SHAPE } from "../../const";
import { initializeQuillFontSupport } from "../../lib/SmartObject/components/Svg/FloatingTextEditor/quillConfig";

interface RightPanelProps {
  lessonPages: any[];
  currentlyDisplayedPageIndex: number;
  lessonVersionId: number;
  isModalShown: boolean;
}

const selectedPanelButtonStyle = {
  background: "var(--buttons-color)",
  color: "white",
};
const nonSelectedPanelButtonStyle = {
  cursor: "pointer",
};

function RightPanel({ currentlyDisplayedPageIndex, lessonPages, lessonVersionId, isModalShown }: RightPanelProps) {
  const lessonPagesDispatch = useLessonPagesDispatch();
  const { selectedPanel } = useLessonPagesState();
  const { selectedObjects } = useObjectsState();
  const previouslySelectedObject = useRef<string | null>(null);
  // TODO: Remove this once we have a way to check if the page is read only
  const { currentAggregationOrder } = useLessonPage();
  const readOnly = currentAggregationOrder !== null;
  const [, setMiscUI] = useMiscUI();

  const selectedObject: BaseObject | undefined = selectedObjects[0];
  const isSmartObject = selectedObject?.type === "smartObject";
  const isPanoramic = selectedObject?.type === "panoramic";
  const isShape = selectedObject?.type === OBJECT_TYPE_SHAPE;
  const isImage = selectedObject?.type === "pageImage";
  const isFreeFormPoly = selectedObject?.type === "freeFormPoly";

  const hasAdvancedPanel = isPanoramic || isSmartObject || isShape || isFreeFormPoly;

  /**
   * This initialization is needed so that the fonts are registered
   * and are not stripped after the advanced panel editor is mounted.
   * Since the Quill editor has an initial mount that parses the provided text.
   *
   * It is mounted here since this is the closest component to the Quill editor.
   * that is always mounted on the application.
   */
  useEffect(() => {
    initializeQuillFontSupport();
  }, []);

  /**
   * This useEffect works so that if another object is selected
   * and the panel is in the `advanced` tab it will switch to
   * the properties tab
   */
  useEffect(() => {
    setMiscUI({ type: "OBJECT_LOCKED", payload: selectedPanel === "advanced" });

    if (selectedPanel === "properties") {
      setMiscUI({
        type: "SET_SELECTED_MASK_ID",
        payload: null,
      });
    }

    if (selectedObject && !previouslySelectedObject.current) {
      previouslySelectedObject.current = selectedObject.objectId;
      return;
    }
    if (selectedPanel !== "advanced") return;
    if (!selectedObject || previouslySelectedObject.current !== selectedObject.objectId) {
      previouslySelectedObject.current = null;
      lessonPagesDispatch({
        type: LessonPagesActions.UPDATE_SELECTED_PANEL,
        payload: "properties",
      });
    }
  }, [selectedObject?.objectId, JSON.stringify(previouslySelectedObject.current), selectedPanel]);

  const handleTabClick = (payload: "properties" | "page" | "advanced") => {
    lessonPagesDispatch({
      type: LessonPagesActions.UPDATE_SELECTED_PANEL,
      payload,
    });
  };

  return (
    <div className="right-panel">
      <div className="selection-container">
        <div
          style={selectedPanel === "page" ? selectedPanelButtonStyle : nonSelectedPanelButtonStyle}
          onClick={() => {
            handleTabClick("page");
            lessonPagesDispatch({
              type: LessonPagesActions.UPDATE_SELECTED_PANEL,
              payload: "page",
            });
          }}
        >
          Page
        </div>
        <div
          style={selectedPanel === "properties" ? selectedPanelButtonStyle : nonSelectedPanelButtonStyle}
          onClick={() => {
            handleTabClick("properties");
            lessonPagesDispatch({
              type: LessonPagesActions.UPDATE_SELECTED_PANEL,
              payload: "properties",
            });
          }}
        >
          Properties
        </div>
        {hasAdvancedPanel && (
          <div
            style={selectedPanel === "advanced" ? selectedPanelButtonStyle : nonSelectedPanelButtonStyle}
            onClick={() => {
              lessonPagesDispatch({
                type: LessonPagesActions.UPDATE_SELECTED_PANEL,
                payload: "advanced",
              });
            }}
          >
            Advanced
          </div>
        )}
      </div>
      <div className="selected-container" style={{ overflow: isImage || isSmartObject ? "auto" : "hidden" }}>
        {selectedPanel === "page" && (
          <PageEditorActionLister
            readOnly={readOnly}
            lessonPages={lessonPages}
            currentIndex={currentlyDisplayedPageIndex}
            lessonVersionId={lessonVersionId}
          />
        )}
        {selectedPanel === "properties" && <Properties readOnly={readOnly} isModalShown={isModalShown} />}
        {selectedPanel === "advanced" && <AdvancedPanel readOnly={readOnly} />}
      </div>
    </div>
  );
}

export default RightPanel;
